/**
 * Auto-appends an attribution line whenever a visitor copies more than
 * ~5 words from a public marketing page. Goal: make our copy survive
 * being pasted into AI prompts, content farms, blog drafts, and slack
 * messages.
 *
 * Scope: ONLY runs on the public marketing surfaces (Landing, About,
 * Strategy, Quiz, Contact, Privacy, Terms, MoneyPillars,
 * IncomeStrategies, Links). Skips:
 *   - authed app pages (paying customers shouldn't be annoyed)
 *   - tiny selections (≤ 5 words), so users can copy a single phone
 *     number, email address, or URL without the attribution suffix
 *     getting in their way.
 *
 * Pattern: HTML clipboard rewrite on `copy` event. Preserves the
 * visible selection visually (the page doesn't flicker), only changes
 * what lands on the clipboard. Falls back gracefully on browsers that
 * don't support clipboardData.setData().
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Pages where attribution is appended. Anything starting with /app is
// the authed dashboard — exempt. /links is a public marketing surface
// so it's included.
const PUBLIC_PATH_PREFIXES = [
  "/", "/about", "/strategy", "/habit-loop-strategy", "/quiz", "/contact",
  "/privacy", "/terms", "/links", "/pillars", "/income-strategies",
  "/4-pillars", "/wealth-blueprint",
];

const MIN_WORDS_TO_ATTRIBUTE = 6;
const SUFFIX = "\n\n— from financialhabitstosucceed.com (© Financial Habits to Succeed. All rights reserved.)";

function isPublicPath(pathname) {
  // Authed app pages start with /app or /admin — never rewrite their copy.
  if (pathname.startsWith("/app") || pathname.startsWith("/admin")) return false;
  // Everything else with a Landing-style layout is public.
  return PUBLIC_PATH_PREFIXES.some((p) =>
    pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?"),
  );
}

function handleCopy(e) {
  try {
    const selection = window.getSelection();
    if (!selection) return;
    const text = selection.toString();
    if (!text) return;
    // Skip tiny selections — single words / phone numbers / emails.
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < MIN_WORDS_TO_ATTRIBUTE) return;
    // Some browsers don't expose `clipboardData` on the event (e.g.
    // older Safari). Fall back to the native copy in that case.
    if (!e.clipboardData) return;
    e.preventDefault();
    // Plain-text channel — used by AI prompts, terminals, Slack, etc.
    e.clipboardData.setData("text/plain", text + SUFFIX);
    // HTML channel — used by Google Docs, Word, Notion. We escape the
    // user-selected text so nothing weird happens with HTML-y copy.
    const escapedHtml = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br>");
    e.clipboardData.setData(
      "text/html",
      `<div>${escapedHtml}</div>` +
        `<p style="color:#787671;font-size:12px;margin-top:8px;">` +
        `— from <a href="https://financialhabitstosucceed.com">financialhabitstosucceed.com</a> ` +
        `(© Financial Habits to Succeed. All rights reserved.)</p>`,
    );
  } catch {
    // Never break copy — silently fall back to native behavior.
  }
}

/**
 * Mount once anywhere inside <BrowserRouter>. No DOM output.
 */
export default function CopyAttribution() {
  const location = useLocation();
  useEffect(() => {
    if (!isPublicPath(location.pathname)) return;
    document.addEventListener("copy", handleCopy);
    return () => document.removeEventListener("copy", handleCopy);
  }, [location.pathname]);
  return null;
}
